import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  useTheme,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { motion } from 'framer-motion';

const MotionCard = motion(Card);

function Portfolio({ wallet, portfolio, coins, achievements }) {
  const theme = useTheme();

  const calculateTotalValue = () => {
    return portfolio.reduce((total, position) => {
      const coin = coins.find(c => c.id === position.coinId);
      return total + (position.amount * coin.price);
    }, 0);
  };

  const calculateProfitLoss = (position) => {
    const coin = coins.find(c => c.id === position.coinId);
    const profitLoss = ((coin.price - position.avgPrice) / position.avgPrice) * 100;
    return profitLoss;
  };

  return (
    <Container maxWidth="lg" sx={{ mb: 4 }}>
      <Typography variant="h4" component="h2" gutterBottom sx={{ 
        fontWeight: 700,
        mb: 4,
        background: 'linear-gradient(45deg, #4CAF50 30%, #81C784 90%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
      }}>
        Your Portfolio
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            sx={{
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
              border: '1px solid rgba(255, 255, 255, 0.18)',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <AccountBalanceWalletIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h6">Portfolio Value</Typography>
                  <Typography variant="h4" color="primary">
                    ${(wallet + calculateTotalValue()).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="subtitle1" gutterBottom>
                Cash: ${wallet.toFixed(2)}
              </Typography>
              
              {portfolio.length > 0 ? (
                <List>
                  {portfolio.map((position, index) => {
                    const coin = coins.find(c => c.id === position.coinId);
                    const profitLoss = calculateProfitLoss(position);
                    
                    return (
                      <React.Fragment key={position.coinId}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle1">{position.name}</Typography>
                                <Chip
                                  label={`${profitLoss.toFixed(2)}%`}
                                  color={profitLoss >= 0 ? 'success' : 'error'}
                                  size="small"
                                />
                              </Box>
                            }
                            secondary={
                              <>
                                Amount: {position.amount} | Avg Price: ${position.avgPrice.toFixed(4)} | 
                                Current: ${coin.price.toFixed(4)}
                              </>
                            }
                          />
                        </ListItem>
                        {index < portfolio.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              ) : (
                <Typography color="text.secondary" textAlign="center" py={4}>
                  No coins in portfolio yet! Start trading! 💼
                </Typography>
              )}
            </CardContent>
          </MotionCard>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            sx={{
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
              border: '1px solid rgba(255, 255, 255, 0.18)',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <EmojiEventsIcon sx={{ fontSize: 40, mr: 2, color: 'warning.main' }} />
                <Typography variant="h6">Achievements</Typography>
              </Box>
              
              {achievements.length > 0 ? (
                <List>
                  {achievements.map((achievement, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemText primary={achievement} />
                      </ListItem>
                      {index < achievements.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" textAlign="center" py={4}>
                  No achievements yet! Keep trading! 🏆
                </Typography>
              )}
            </CardContent>
          </MotionCard>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Portfolio; 